import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO, subDays } from 'date-fns';
import DateQuickPresets from '../../components/DateQuickPresets';

const TAB_ITEMS = [
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'sem_plano', label: 'Sem Plano' },
  { key: 'sem_centro', label: 'Sem Centro' },
  { key: 'sem_conta_bancaria', label: 'Sem Conta' },
];

const QualidadeDados = () => {
  const hoje = new Date();
  const [carregando, setCarregando] = useState(false);
  const [tabAtiva, setTabAtiva] = useState('pendentes');
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: format(subDays(hoje, 90), 'yyyy-MM-dd'),
    data_fim: format(hoje, 'yyyy-MM-dd'),
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
    limite: 300,
  });
  const [dados, setDados] = useState({
    indicadores: {
      total_lancamentos: 0,
      confirmados: 0,
      pendentes: 0,
      percentual_conciliado: 0,
      sem_plano: 0,
      sem_centro: 0,
      sem_conta_bancaria: 0,
    },
    listas: {
      pendentes: [],
      sem_plano: [],
      sem_centro: [],
      sem_conta_bancaria: [],
    },
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros de qualidade:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/qualidade-dados', { params: filtros });
      const payload = response.data || {};
      setDados({
        indicadores: payload.indicadores || {
          total_lancamentos: 0,
          confirmados: 0,
          pendentes: 0,
          percentual_conciliado: 0,
          sem_plano: 0,
          sem_centro: 0,
          sem_conta_bancaria: 0,
        },
        listas: payload.listas || {
          pendentes: [],
          sem_plano: [],
          sem_centro: [],
          sem_conta_bancaria: [],
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar qualidade dos dados');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const linhasAtivas = useMemo(() => dados.listas?.[tabAtiva] || [], [dados.listas, tabAtiva]);

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Qualidade dos Dados</h1>
          <p className="text-xs text-gray-500 mt-1">Sem plano e sem centro consideram apenas receita e despesa.</p>
          <p className="text-gray-600">Conciliação operacional e inconsistências de cadastro</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="range"
            startDate={filtros.data_inicio}
            endDate={filtros.data_fim}
            onSelect={({ startDate, endDate }) =>
              setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
            }
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="form-label">Data Inicial</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Data Final</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_fim}
                onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Limite por Lista</label>
              <input
                type="number"
                min="50"
                max="1000"
                className="form-input"
                value={filtros.limite}
                onChange={(e) => setFiltros((old) => ({ ...old, limite: Number(e.target.value || 300) }))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">% Conciliado</p>
            <p className={`text-2xl font-bold ${Number(dados.indicadores.percentual_conciliado || 0) >= 95 ? 'text-success-700' : 'text-warning-600'}`}>
              {Number(dados.indicadores.percentual_conciliado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%
            </p>
            <p className="text-xs text-gray-500">Confirmados: {dados.indicadores.confirmados} • Pendentes: {dados.indicadores.pendentes}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Sem Plano</p>
            <p className="text-2xl font-bold text-danger-700">{dados.indicadores.sem_plano}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Sem Centro</p>
            <p className="text-2xl font-bold text-danger-700">{dados.indicadores.sem_centro}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Sem Conta Bancaria</p>
            <p className="text-2xl font-bold text-danger-700">{dados.indicadores.sem_conta_bancaria}</p>
            <p className="text-xs text-gray-500">Base: {dados.indicadores.total_lancamentos} lancamentos, incluindo transferencias</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex gap-2">
            {TAB_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => setTabAtiva(item.key)}
                className={`px-3 py-1 rounded text-sm font-medium ${tabAtiva === item.key ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'}`}
              >
                {item.label} ({(dados.listas?.[item.key] || []).length})
              </button>
            ))}
          </div>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descricao</th>
                  <th>Status</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {linhasAtivas.map((item) => (
                  <tr key={item.id}>
                    <td>{format(parseISO(item.data_lancamento), 'dd/MM/yyyy')}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : item.tipo === 'despesa' ? 'text-danger-700' : 'text-gray-700'}>
                      {item.tipo}
                    </td>
                    <td className="max-w-[480px] truncate" title={item.descricao}>{item.descricao}</td>
                    <td>{item.status}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : item.tipo === 'despesa' ? 'text-danger-700' : 'text-gray-700'}>
                      R$ {formatarValor(item.valor)}
                    </td>
                  </tr>
                ))}
                {linhasAtivas.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-8">
                      Sem ocorrencias para este critério no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualidadeDados;
